import type { JourDuCalendrier } from '../partage/api/types';
import { cartesDuJour, toucherDUneCarte, type CarteDuJour, type ToucherDUneCarte } from './cartesDeLaSemaine';

/**
 * Le mois et le jour ouvert de l'Agenda au téléphone (chantier `telephone-journee-agenda`,
 * ticket 06, décisions 18 et 19 de la revue, maquette `design/telephone.html`, piste « Agenda »,
 * « Le mois » et « Un jour ouvert »). Les choses d'un jour sont les cartes de la semaine
 * (`cartesDeLaSemaine.ts`) : rien n'est calculé ici que leur tri en barres et en rubriques.
 */

/** Au-delà, la case du mois dit « +N » : le jour touché dit le reste. */
export const BARRES_AU_PLUS = 3;

export interface BarresDuJour {
  /** Une barre par chose posée, à la couleur de son projet ; la verte pour ta journée. */
  barres: CarteDuJour[];
  /** Combien ne tiennent pas dans la case. */
  deplus: number;
}

/**
 * Les barres d'une case du mois : une par chose posée, dans l'ordre de la semaine, trois au plus,
 * puis « +N ». Ta journée prévue garde toujours sa barre verte, la dernière : c'est elle qui dit
 * « ce jour est déjà planifié ».
 */
export function barresDuJour(j: JourDuCalendrier): BarresDuJour {
  const cartes = cartesDuJour(j);
  if (cartes.length <= BARRES_AU_PLUS) return { barres: cartes, deplus: 0 };
  const journee = cartes.find((c) => c.sorte === 'journee');
  const autres = cartes.filter((c) => c.sorte !== 'journee');
  const barres = journee ? [...autres.slice(0, BARRES_AU_PLUS - 1), journee] : autres.slice(0, BARRES_AU_PLUS);
  return { barres, deplus: cartes.length - barres.length };
}

/** Le jour touché dans le mois, montré dessous : ses choses, et ce qu'il manque de dire. */
export interface ApercuDuJour {
  cartes: CarteDuJour[];
  /** « Pas de journée prévue » : aujourd'hui ou à venir, sans journée. */
  pasDeJournee: boolean;
  /** « Rien de posé ce jour-là » : un jour passé où il n'y a rien. */
  rien: boolean;
}

export function apercuDuJour(j: JourDuCalendrier): ApercuDuJour {
  const cartes = cartesDuJour(j);
  return { cartes, pasDeJournee: !j.passe && !j.journee, rien: j.passe && cartes.length === 0 };
}

/**
 * Toucher une chose du jour touché : comme dans la semaine, sauf la réponse pas venue, qui ouvre
 * le jour en entier, où vit son « Relancer ».
 */
export function toucherDansLeMois(c: CarteDuJour, j: Pick<JourDuCalendrier, 'estAujourdhui'>): Exclude<ToucherDUneCarte, 'gestes'> {
  const suite = toucherDUneCarte(c, j);
  return suite === 'gestes' ? 'jour' : suite;
}

/** Un jour ouvert, en trois rubriques : ce qui est imposé, ta journée, ce qui doit arriver. */
export interface RubriquesDuJour {
  /** Les rendez-vous, à leur heure. */
  impose: CarteDuJour[];
  /** Ta journée prévue, résumée (« 2 tâches · dès 14 h ») ; `null` sans journée. */
  journee: CarteDuJour | null;
  /** Les échéances (⚑), puis les réponses attendues (⏸), celles pas venues d'abord. */
  doitArriver: CarteDuJour[];
}

export function rubriquesDuJour(j: JourDuCalendrier): RubriquesDuJour {
  const cartes = cartesDuJour(j);
  return {
    impose: cartes.filter((c) => c.sorte === 'rdv'),
    journee: cartes.find((c) => c.sorte === 'journee') ?? null,
    doitArriver: cartes.filter((c) => c.sorte === 'echeance' || c.sorte === 'attente'),
  };
}
